import { Routes } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Location } from 'history';
import React from 'react';
import GoogleButton from 'react-google-button';
import { useHistory } from 'react-router-dom';

import { ReactComponent as LoginArtSvg } from '../../assets/login_art.svg';
import { colorPalette, spacings, TextSizesEnum } from '../../theme';
import { PlexTypography } from '../shared/primitives/PlexTypography';

/**
 * Generates the google sign-in url which may contain a signup email and a redirect as params
 * @param location the current page location which is part of the router dom history
 */
export function generateSignInURL(location: Location<unknown>): string {
  const signInParams = new URLSearchParams();
  const searchParams = new URLSearchParams(location.search);
  const signupEmail = searchParams.get(Routes.SIGNUP_EMAIL_PARAM);
  searchParams.delete(Routes.SIGNUP_EMAIL_PARAM);
  const searchParamsString = searchParams.toString();
  signInParams.append(
    'redirect',
    `${location.pathname}${searchParamsString.length > 0 ? `?${searchParamsString}` : ''}`
  );
  if (signupEmail) {
    signInParams.append(Routes.SIGNUP_EMAIL_PARAM, signupEmail);
  }
  return `${Routes.LOGIN_API_PATH}?${signInParams.toString()}`;
}

export const SignInPage = (): React.ReactElement => {
  const classes = useStyles();
  const history = useHistory();
  const signInURL = generateSignInURL(history.location);

  return (
    <div className={classes.root}>
      <div className={classes.rootContent}>
        <div className={classes.artContainer}>
          <div>
            <LoginArtSvg />
          </div>
          <PlexTypography
            className={classes.artTitle}
            text={'Align Product and\nGo-to-Market Teams with Plex'}
            size={TextSizesEnum.XLarge}
          />
          <PlexTypography
            className={classes.artMessage}
            text={
              'Product information changes fast. Filter out the noise\nfrom the signal, and bring clarity to every stakeholder.'
            }
            size={TextSizesEnum.NormalPlus}
          />
        </div>
        <div className={classes.loginContainer}>
          <div className={classes.loginContent}>
            <PlexTypography className={classes.loginTitle} text={'Sign In'} size={TextSizesEnum.XXLarge} />
            <PlexTypography
              className={classes.loginMessage}
              text={'Just sign in if you have an account in here. Enjoy your time.'}
              size={TextSizesEnum.Normal}
            />
            <GoogleButton
              className={classes.googleLoginButton}
              onClick={(): unknown => (window.location.href = signInURL)}
              type="light"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      overflowY: 'hidden',
      overflowX: 'auto',
      height: '100vh',
      width: '100%',
      paddingTop: 80,
    },
    rootContent: {
      display: 'flex',
      flexDirection: 'row',
      overflowY: 'auto',
      width: '100%',
      height: '100%',
    },
    artContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      backgroundColor: '#fff',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 48,
      paddingRight: 48,
    },
    artTitle: {
      color: '#464663',
      fontWeight: 'bold',
      textAlign: 'center',
      marginTop: spacings.xlarge,
    },
    artMessage: {
      color: colorPalette.purpleGray._500,
      textAlign: 'center',
      marginTop: spacings.normal,
    },
    loginContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colorPalette.general.backgroundGray,
    },
    loginContent: {
      display: 'flex',
      flexDirection: 'column',
      width: 404,
      marginLeft: 48,
      marginRight: 48,
    },
    loginTitle: {
      color: '#464663',
      fontWeight: 600,
      fontSize: 44,
      lineHeight: '53px',
    },
    loginMessage: {
      color: colorPalette.purpleGray._500,
      marginTop: spacings.small,
    },
    googleLoginButton: {
      marginTop: 20,
    },
  })
);
